import type { TooltipProps } from '@any-ui-react/core';
import type { CSSProperties, PropsWithChildren, ReactElement, ReactNode } from 'react';
import type { TableSelection } from '../../../types';
import { NoResult } from '../no-result';
import { DEFAULT_PAGE_SIZE } from './constants';
import { TableSelectionProvider } from './selection';
import { TableDesktop } from './table/TableDesktop';
import { TableDesktopContainer } from './table/TableDesktopContainer';
import { TableSkeleton } from './table/TableSkeleton';

export interface CellRenderCtx {
  expanded?: boolean;
  toggleDetailPanel?: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export interface TableLayout<T> {
  headerRender: () => ReactNode;
  cellRender: (row: T, rowIndex: number, ctx?: CellRenderCtx) => ReactNode;
  checkboxTooltip?: (row: T) => TooltipProps;
  footer?: () => ReactNode;
  accessor: string;
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  truncated?: boolean;
  truncatedWithoutEllipsis?: boolean;
  thClassName?: string;
  /** A function receives the row, or `null` for the footer and skeleton cells, which have no row. */
  tdClassName?: string | ((row: T | null) => string);
  skeletonTemplate?: ReactNode;
  checkboxDisabled?: (row: T) => boolean;
  expandable?: boolean;
}

interface TableSelectable<T> {
  byField: keyof T;
  aside?: ReactNode;
  total: number;
  rowDisabled?: (row: T) => boolean;
  allPagesSelectable?: boolean;
}

export interface TableProps<T> {
  allowFloatingHeader?: boolean;
  borderBottomLastRow?: boolean;
  data?: ReadonlyArray<T>;
  emptyContainerClassName?: string;
  expandedRows?: Set<string>;
  footer?: ReactNode;
  fullWidth?: boolean;
  headerClassName?: string;
  hoverable?: boolean;
  layout: ReadonlyArray<TableLayout<T>>;
  loading?: boolean;
  notFoundTemplate?: ReactNode;
  numOfStickyCols?: number;
  page?: number;
  renderDetailPanel?: (row: T, index: number) => ReactNode;
  rowClassName?: string;
  trClassName?: string | ((row: T, index: number) => string);
  scrollContainerClassName?: string;
  selectable?: TableSelectable<T>;
  selection?: TableSelection<T>;
  skeletonCount?: number;
  tableLayout?: CSSProperties['tableLayout'];
  wrapperClassName?: string;
  onClick?: (row: T) => void;
  onSelectionChange?: (selection: TableSelection<T>) => void;
  onToggleExpand?: (rowId: string) => void;
}

export const Table = <T extends Record<string, unknown>>({
  data = [],
  emptyContainerClassName = 'border-gray-2 border-t text-center',
  expandedRows = new Set(),
  footer,
  loading,
  notFoundTemplate,
  numOfStickyCols,
  onSelectionChange,
  onToggleExpand,
  page,
  selectable,
  selection,
  skeletonCount = DEFAULT_PAGE_SIZE,
  wrapperClassName = 'border-none shadow-none lg:rounded-b',
  ...props
}: PropsWithChildren<TableProps<T>>): ReactElement => (
  <TableSelectionProvider
    data={data}
    page={page}
    selectable={selectable}
    selection={selection}
    onSelectionChange={onSelectionChange}
  >
    <div className={wrapperClassName}>
      <TableSkeleton
        expandedRows={expandedRows}
        loading={loading}
        numOfStickyCols={numOfStickyCols}
        skeletonCount={skeletonCount}
        onToggleExpand={onToggleExpand}
        {...props}
      >
        {data.length === 0 ? (
          <div className={emptyContainerClassName}>
            {notFoundTemplate || (
              <NoResult description="Try changing the filters or search terms" title="No data found" />
            )}
          </div>
        ) : (
          <TableDesktopContainer<T>
            data={data}
            expandedRows={expandedRows}
            numOfStickyCols={numOfStickyCols}
            onToggleExpand={onToggleExpand}
            {...props}
          >
            <TableDesktop<T> data={data} expandedRows={expandedRows} onToggleExpand={onToggleExpand} {...props} />
          </TableDesktopContainer>
        )}
      </TableSkeleton>
    </div>
    {footer}
  </TableSelectionProvider>
);
